import React, { useState, useEffect, useRef, useCallback } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterAbility,
  CharacterFeature,
  CharacterItem,
  // CharacterItem,
  CharacterSpell,
  ItemLimitation,
  PseudoItem,
  Selectable,
  SpellLimitation,
  SummonOption2,
} from "@dndModels";
import { 
  ABILITY_SCORES, 
  ALL_DRAGONS, 
  DAMAGE_TYPES, 
  DICE_SIZE_OPTIONS, 
  DRAGON_TYPES, 
  RARITY_NUM_TO_STR_MAP, 
  SCHOOLS, 
} from "@dndModels/Constants";
import { ModelBase } from "@models";

import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import DandDAPI from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import LoadingBox from "@components/input/LoadingBox";
// import FilterBox from '../../FilterBox';
import SelectSpellBox from "@dndComponents/select/SelectSpellBox";
import CharacterListOptionsInput from "@dndComponents/character/misc/CharacterListOptionsInput";
import StringBox from "@components/input/strings/StringBox";
import { DataType } from '@dndModels/Types';

interface Props {
  character: Character;
  obj: CharacterFeature;
  onChange: (value: string[]) => void; 
}

const char_util = DandDCharacterUtilities.getInstance();
const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CharacterSelectableInput({ 
  character,
  obj,
  onChange
}: Props) {
  const fos = obj.feature_options as string[];
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [reloading_ui, setReloadingUI] = useState(false);
  const [pseudo_items, setPseudoItems] = useState<PseudoItem[]>([]);
  const [char_spells, setCharSpells] = useState<(CharacterSpell | CharacterAbility)[]>([]);
  const [pi_options, setPIOptions] = useState<PseudoItem[]>([]);
  const [char_spell_options, setCharSpellOptions] = useState<CharacterSpell[]>([]);
  const [changing, setChanging] = useState(false);
  const [selected, setSelected] = useState(fos);
  const [change_time, setChangeTime] = useState(0);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = useCallback(() => {
    setReloading(true);
  }, [setReloading]);

  const calc_use_list = useCallback(() => {
    const the_filter = {};
    if (obj.floating_feature instanceof Selectable) {
      // debugger;
      if (obj.floating_feature.type === "Item") {
        const filtered_pseudo_items = api.filter("", pseudo_items, the_filter);
        // if (proficient !== null) {
        //   filtered_pseudo_items = filtered_pseudo_items.filter(o => proficient === char_util.proficient(character, o));
        // }
        setPIOptions(filtered_pseudo_items);
        if (change_time === 0) {
          setReloadingUI(true);
        }
        setChangeTime(common_util.get_time_number());
      } else if (obj.floating_feature.type === "Spell") {
        // const filtered_spells = api.filter("", spells, the_filter);
        // setSpellOptions(filtered_spells);
      } else if (obj.floating_feature.type === "Character Spell") {
        const filtered_spells = api.filter("", char_spells, the_filter);
        setCharSpellOptions(filtered_spells);
        setReloadingUI(true);
      }
    }
  }, [char_spells, obj, pseudo_items, change_time]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
      calc_use_list();
    }
  }, [reloading, setReloading, calc_use_list]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      // debugger;
      if (obj.floating_feature instanceof Selectable) {
        if (obj.floating_feature.type === "Item") {
          const limitation = char_util.findLimitation(obj.floating_feature.limitations, character, obj.source_true_id);
          let new_pseudo_items: PseudoItem[] = [];
          if (limitation instanceof ItemLimitation) {
            new_pseudo_items = char_util.getPseudoItems({ limitation });
          }
          setPseudoItems(new_pseudo_items);
          setReloading(true);
        } else if (obj.floating_feature.type === "Character Spell") {
          const limitation = char_util.findLimitation(obj.floating_feature.limitations, character, obj.source_true_id);
          if (limitation instanceof SpellLimitation) {
            // console.log(character.mind.spells);
            // console.log(character.mind.spells.map(o => o.name));
            const new_spells = char_util.getCharSpells(character, limitation);
            setCharSpells(new_spells);
            setReloading(true);
          }
        }
      }
      character.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    }
  }, [character, obj, fetched, setFetched, mounted, recalculated]);
  
  useEffect(() => {
    if (reloading_ui) {
      setReloadingUI(false);
    }
  }, [reloading_ui, setReloadingUI]);

  useEffect(() => {
    if (changing) {
      setChanging(false);
      onChange(selected);
    }
  }, [changing, setChanging, selected, onChange]);
  
  if (!fetched || reloading_ui) {
    return (<LoadingBox />);
  } else if (obj.floating_feature instanceof Selectable) {
    const selectable = obj.floating_feature as Selectable;
    if (["Ability Score", "Damage Type", "School of Magic", "Dragon Type", "Dragon Breed"].includes(selectable.type)) {
      let options = selectable.options;
      if (options.length === 0) {
        switch(selectable.type) {
          case "Ability Score": options = ABILITY_SCORES; break;
          case "Damage Type": options = DAMAGE_TYPES; break;
          case "School of Magic": options = SCHOOLS; break;
          case "Dragon Type": options = DRAGON_TYPES; break;
          case "Dragon Breed": options = ALL_DRAGONS; break;
        }
      }
      return (
        <SelectStringBox 
          name={selectable.the_key}
          value={obj.feature_options[0] as string}
          color={obj.feature_options[0] === "" ? "blue" : ""}
          options={options}
          onChange={(changed: string) => {
            onChange([changed]);
          }}
        />
      );
    } else if (selectable.type === "Item") {
      const selected_options = obj.feature_options as string[];
      // TODO: Change this to CharacterRowListInput
      console.log(selected_options);
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 12
            }}>
            <CharacterListOptionsInput 
              character={character} 
              data_type="PseudoItem"
              selected={selected_options.filter(o => o !== "")}
              choose_max={selected_options.length}
              use_objects={pi_options}
              change_time={change_time}
              getSmallDetails={(obj: ModelBase | CharacterSpell | CharacterItem | SummonOption2 | PseudoItem) => {
                if (obj instanceof PseudoItem) {
                  return RARITY_NUM_TO_STR_MAP[obj.rarity];
                }
                return "";
              }}
              onChange={(changed: string[]) => {
                // debugger;
                const new_options = [...changed];
                while (new_options.length < selected_options.length) {
                  new_options.push("");
                }
                onChange(new_options);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
        </Grid>
      );
    } else if (selectable.type === "Weapon Type") {
      return (
        <SelectStringBox 
          name={selectable.the_key}
          value={obj.feature_options[0] as string}
          color={obj.feature_options[0] === "" ? "blue" : ""}
          options={selectable.options}
          onChange={(changed: string) => {
            onChange([changed]);
          }}
        />
      );
    } else if (selectable.type === "Spell") {
      const count = char_util.value(selectable.count, { obj: character, source_id: obj.source_true_id });
      const limitation = char_util.findLimitation(obj.floating_feature.limitations, character, obj.source_true_id);
      if (limitation instanceof SpellLimitation) {
        // console.log(selectable.suggestions);
        return (
          <SelectSpellBox
            name={selectable.the_key}
            spell_limit={limitation}
            values={obj.feature_options as string[]}
            multiple={count !== 1}
            max_selected={count}
            onChange={onChange}
          />
        );
      }
    } else if (selectable.type === "Character Spell") {
      // console.log(char_spell_options);
      const count = char_util.value(selectable.count, { obj: character, source_id: obj.source_true_id });
      return (
        <CharacterListOptionsInput
          data_type="char_spell"
          character={character}
          choose_max={count} 
          use_objects={char_spell_options}
          selected={selected} 
          force_selectable
          onChange={(changed: string[]) => {
            setSelected(changed);
            setChanging(true);
          }} 
          // getDisabledReasons={(obj: ModelBase | CharacterSpell | CharacterItem) => {
          //   if (obj instanceof PseudoItem) {
          //     // if (obj.name.includes("Ascendant Step")) {
          //     //   debugger;
          //     // }
          //     const reasons = char_util.fitLimits(character, obj.limitations, "subrace");
          //     return reasons === "true" ? "" : reasons;
          //   }
          //   return "";
          // }}
        />
      );
    } else if (selectable.type === "Strings") {
      const options = selectable.options;
      // debugger;
      return (
        <SelectStringBox 
          name={selectable.the_key}
          value={obj.feature_options[0] as string}
          color={obj.feature_options[0] === "" ? "blue" : ""}
          options={options}
          onChange={(changed: string) => {
            onChange([changed]);
          }}
        />
      );
    } else if (selectable.type === "Number") {
      return (
        <StringBox 
          name={selectable.the_key}
          value={obj.feature_options[0] as string}
          color={obj.feature_options[0] === "" ? "blue" : ""}
          type="number"
          onBlur={(changed: string) => {
            onChange([changed]);
          }}
        />
      );
    } else if (selectable.type === "Dice Size") {
      const options = selectable.options;
      // debugger;
      return (
        <SelectStringBox 
          name={selectable.the_key}
          value={obj.feature_options[0] as string}
          color={obj.feature_options[0] === "" ? "blue" : ""}
          options={options.length === 0 ? DICE_SIZE_OPTIONS : options}
          onChange={(changed: string) => {
            onChange([changed]);
          }}
        />
      );
    } else {
      const options = selectable.options;
      let data_type: DataType | "" = "";
      switch(selectable.type) {
        case "Spell List": data_type = "spell_list"; break;
        case "Race": data_type = "race"; break;
      }
      if (data_type === "") { 
        return <></>;
      }
      // debugger;
      return (
        <SelectObjectBox 
          name={selectable.the_key}
          value={obj.feature_options[0] as string}
          color={obj.feature_options[0] === "" ? "blue" : ""}
          data_type={data_type}
          filter={ options.length > 0 ? { _id: { "in": options }} : {} }
          onChange={(changed: string) => {
            onChange([changed]);
          }}
        />
      );
    }
  }
  return <></>;
}
